import {
    addKeyMouseEvents,
    removeMouseEvents,
} from '@utils/key-mouse-event';
import { reaction } from 'mobx';
import { IBaseActionsInjectable, IBaseShortcutKeyManager, IBaseShortcutKeyManagerInjectable, IBaseShortcutKeyServices } from '../interface';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import BaseShortcutActions from './actions';
import { ShortcutActionName } from '../constants';
import { EXTRA_DIV_FOR_ACCESSIBILITY } from '@src/slide/application/service/accessibility-manager/core/accessibility-constants';

export default abstract class BaseShortcutKeyManager implements IBaseShortcutKeyManager {
    protected services: IBaseShortcutKeyServices;
    // manager 自身的异化逻辑
    protected readonly injectable?: IBaseShortcutKeyManagerInjectable;
    // action 的异化逻辑
    protected readonly actionsInjectable?: IBaseActionsInjectable;
    // 存储正常快捷键与处理逻辑映射的配置
    private shortcutConfig = {};
    // 存储冒泡快捷键与处理逻辑映射的配置
    private bubblingShortcutConfig = {};
    private actions: BaseShortcutActions;

    public constructor(injectable?: IBaseShortcutKeyManagerInjectable, actionsInjectable?: IBaseActionsInjectable) {
        this.injectable = injectable;
        this.actionsInjectable = actionsInjectable;
    }

    public setServicesDelegates(services: Partial<IBaseShortcutKeyServices>) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.actions?.setServicesDelegates(services);
    }

    public init() {
        this.actions = this.createShortcutActions();
        // 监听权限变化并更新快捷键绑定
        reaction(
            () => this.getEditorState(),
            (editorState) => {
                this.updateShortcutBinding(editorState);
            },
            // 立即执行一次
            { fireImmediately: true },
        );
    }

    public shouldShortcutKeyActive() {
        if (this.isAiSidebarActive()) return false;

        // 若编辑区上有浮层（如播放态、远程演示、各种 Modal 弹窗），则不应响应编辑态快捷键
        if (this.actions.hasFloatContent()) {
            return false;
        }
        // 可以编辑的dom结构active状态有value值，参考https://stackoverflow.com/questions/25766664/check-if-dom-element-is-editable-allow-user-input-with-pure-js/25767396#25767396
        if (!this.isTextEditorActive() && this.isEditableDomActive()) {
            return false;
        }
        return true;
    }

    protected handleAction = (actionName: string, e: KeyboardEvent | MouseEvent) => {
        if (!this.shouldShortcutKeyActive() && !this.isUndoRedoInChartEditPanel(actionName)) {
            return;
        }
        if (this.services.editingStateManager?.isEditing) {
            return;
        }
        if (this.services.menuManager?.isMenuShown) { // 有菜单弹出，也不响应全局按键
            return;
        }
        return this.actions[actionName]?.(e);
    };

    protected getShortcutConfig() {
        const ctrl = this.getCtrlKey();
        return {
            [`${ctrl}+enter`]: this.handleAction.bind(this, ShortcutActionName.PLAY),
            [`${ctrl}+a`]: this.handleAction.bind(this, ShortcutActionName.SELECTALL),
            [`${ctrl}+z`]: this.handleAction.bind(this, ShortcutActionName.UNDO),
            [`${ctrl}+y`]: this.handleAction.bind(this, ShortcutActionName.REDO),
            ['ctrl+m']: this.handleAction.bind(this, ShortcutActionName.NEWSLIDE_M), // Mac上cmd+m是系统快捷键，这里统一处理成ctrl+m
            backspace: this.handleAction.bind(this, ShortcutActionName.DELETE),
            delete: this.handleAction.bind(this, ShortcutActionName.DELETE),
            enter: this.handleAction.bind(this, ShortcutActionName.NEWSLIDE_ENTER),
            [`${ctrl}+alt+g`]: this.handleAction.bind(this, ShortcutActionName.ADD_GROUP),
            [`${ctrl}+alt+shift+g`]: this.handleAction.bind(this, ShortcutActionName.REMOVE_GROUP),
            tab: this.handleAction.bind(this, ShortcutActionName.TABACTION),
            [`${ctrl}+p`]: this.handleAction.bind(this, ShortcutActionName.PRINT),
        };
    }

    protected getBubblingShortcutConfig() {
        return {
            up: this.handleAction.bind(this, ShortcutActionName.UP),
            left: this.handleAction.bind(this, ShortcutActionName.LEFT),
            pageup: this.handleAction.bind(this, ShortcutActionName.PAGEUP),
            down: this.handleAction.bind(this, ShortcutActionName.DOWN),
            right: this.handleAction.bind(this, ShortcutActionName.RIGHT),
            pagedown: this.handleAction.bind(this, ShortcutActionName.PAGEDOWN),
            home: this.handleAction.bind(this, ShortcutActionName.HOME),
            end: this.handleAction.bind(this, ShortcutActionName.END),
            wheel: this.handleAction.bind(this, ShortcutActionName.MOUSEWHEEL),
        };
    }

    private bindShortcuts() {
        this.shortcutConfig = this.getShortcutConfig();
        addKeyMouseEvents(this.shortcutConfig);
    }

    private bindBubblingShortcuts() {
        this.bubblingShortcutConfig = this.getBubblingShortcutConfig();
        addKeyMouseEvents(this.bubblingShortcutConfig, { bubble: true });
    }

    private bindNonEditableShortcuts() {
        const ctrl = this.getCtrlKey();
        this.shortcutConfig = {
            [`${ctrl}+enter`]: this.handleAction.bind(this, ShortcutActionName.PLAY),
            [`${ctrl}+a`]: this.handleAction.bind(this, ShortcutActionName.SELECTALL),
            wheel: this.handleAction.bind(this, ShortcutActionName.MOUSEWHEEL),
            tab: this.handleAction.bind(this, ShortcutActionName.TABACTION),
            [`${ctrl}+p`]: this.handleAction.bind(this, ShortcutActionName.PRINT),
        };
        addKeyMouseEvents(this.shortcutConfig);
    }


    private unBindShortcuts() {
        removeMouseEvents(this.shortcutConfig);
        removeMouseEvents(this.bubblingShortcutConfig, { bubble: true });
    }

    private updateShortcutBinding(editorState: typeof PPTEditorStateType[keyof typeof PPTEditorStateType]) {
        this.unBindShortcuts();
        if (editorState === PPTEditorStateType.EDITABLE) {
            this.bindShortcuts();
        } else {
            // copyable 与 selectable 都注册非edit快捷键即可
            this.bindNonEditableShortcuts();
        }
        this.bindBubblingShortcuts();
    }

    private getEditorState() {
        return this.services.userPriviledgeService.pptEditorState;
    }

    private isTextEditorActive() {
        const classNameArr = ['editor-body', 'CopyPasteAssist', EXTRA_DIV_FOR_ACCESSIBILITY];
        if (document.activeElement) {
            const classNameString = document.activeElement.className;
            if ((typeof classNameString === 'string')) {
                // 文本框focus状态时候文本框自己处理快捷键
                // hidden-editor-input 是canvas版本输入框
                if (classNameArr.indexOf(classNameString) !== -1 || classNameString.startsWith('style_hidden-editor-input')) {
                    return true;
                }
            }
        }
        return false;
    }

    private isEditableDomActive() {
        const inputs = ['input', 'textarea'];
        if (document.activeElement) {
            const activeElement = document.activeElement as any;
            if (inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1) {
                // 排除input#type=checkbox
                if (activeElement.tagName.toLowerCase() === 'input'
                && activeElement.type === 'checkbox') {
                    return false;
                }
                return true;
            }

            if (activeElement.value
                || activeElement.isContentEditable) {
                return true;
            }
        }

        return false;
    }

    private isAiSidebarActive() {
        return this.isActiveElementInClassList(['ai-float-window']);
    }

    /**
     * 判断当前 activeElement 的类名是否包含在指定的类名列表中
     */
    private isActiveElementInClassList(classList: string[]): boolean {
        if (!document.activeElement) return false;

        const activeElementClasses = Array.from(document.activeElement.classList);
        return classList.some(className => activeElementClasses.includes(className));
    }

    /**
     * input/textarea 的焦点是否在图表编辑面板里的表格
     */
    private isUndoRedoInChartEditPanel(actionName) {
        const isUndoRedo = actionName === 'undo' || actionName === 'redo';

        return isUndoRedo
            && Boolean(document.activeElement)
            && Boolean(document.querySelector('.slide-spreadsheet')?.contains(document.activeElement));
    }

    protected abstract getCtrlKey(): string;

    protected abstract createShortcutActions(): BaseShortcutActions;
}
